import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase, Worker, Site, Induction } from '../lib/supabase';
import { WorkerForm } from '../components/forms/WorkerForm';
import {
  Users,
  Mail,
  Phone,
  Calendar,
  Award,
  Edit,
  ArrowLeft,
  Building2,
  ClipboardCheck,
  CheckCircle,
  Clock,
  AlertTriangle,
  Shield,
  DollarSign
} from 'lucide-react';

interface WorkerWithDetails extends Worker {
  sites?: Site[];
  inductions?: (Induction & { site?: Site })[];
}

export const WorkerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [worker, setWorker] = useState<WorkerWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      fetchWorkerDetails();
    }
  }, [id]);

  const fetchWorkerDetails = async () => {
    try {
      // Fetch worker details
      const { data: workerData, error: workerError } = await supabase
        .from('workers')
        .select('*')
        .eq('id', id)
        .single();

      if (workerError) throw workerError;

      // Fetch site assignments
      const { data: assignmentsData, error: assignmentsError } = await supabase
        .from('site_assignments')
        .select(`
          *,
          sites!inner(*)
        `)
        .eq('worker_id', id)
        .eq('status', 'active');

      // Fetch inductions for this worker
      const { data: inductionsData, error: inductionsError } = await supabase
        .from('inductions')
        .select(`
          *,
          sites!inner(*)
        `)
        .eq('worker_id', id);

      const sites = assignmentsData?.map(assignment => assignment.sites) || [];
      const inductions = inductionsData?.map(induction => ({
        ...induction,
        site: induction.sites
      })) || [];

      setWorker({
        ...workerData,
        sites,
        inductions
      });
    } catch (error) {
      console.error('Error fetching worker details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateWorker = async (workerData: Partial<Worker>) => {
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('workers')
        .update(workerData)
        .eq('id', id);
      
      if (error) throw error;
      
      setShowEditModal(false);
      fetchWorkerDetails();
    } catch (error) {
      console.error('Error updating worker:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const getInductionStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-600" />;
      case 'expired':
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
      default:
        return <ClipboardCheck className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!worker) {
    return (
      <div className="text-center py-12">
        <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Worker not found</h3>
        <Link to="/workers" className="text-orange-600 hover:text-orange-700">
          Return to Workers
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/workers')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center space-x-3">
            <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{worker.name}</h1>
              <p className="text-gray-600 capitalize">{worker.role.replace('_', ' ')}</p>
            </div>
          </div>
        </div>
        <button
          onClick={() => setShowEditModal(true)}
          className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-700 transition-colors"
        >
          <Edit className="h-5 w-5" />
          <span>Edit Worker</span>
        </button>
      </div>

      {/* Worker Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Worker Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Worker Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <p className="text-sm text-gray-900">{worker.email}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <p className="text-sm text-gray-900">{worker.phone || 'Not provided'}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">White Card Number</label>
                <div className="flex items-center space-x-2">
                  <Award className="h-4 w-4 text-gray-400" />
                  <p className="text-sm text-gray-900">{worker.whitecard_number || 'Not provided'}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <p className="text-sm text-gray-900">{new Date(worker.start_date).toLocaleDateString()}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(worker.status)}`}>
                  {worker.status.charAt(0).toUpperCase() + worker.status.slice(1)}
                </span>
              </div>
              {worker.hourly_pay_rate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Pay Rate</label>
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-green-600" />
                    <p className="text-sm font-semibold text-green-600">${worker.hourly_pay_rate.toFixed(2)}/hr</p>
                  </div>
                </div>
              )}
              {worker.overtime_pay_rate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Overtime Pay Rate</label>
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-orange-600" />
                    <p className="text-sm font-semibold text-orange-600">${worker.overtime_pay_rate.toFixed(2)}/hr</p>
                  </div>
                </div>
              )}
            </div>

            {worker.certifications && worker.certifications.length > 0 && (
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Certifications</label>
                <div className="flex flex-wrap gap-2">
                  {worker.certifications.map((cert, index) => (
                    <span key={index} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm flex items-center space-x-1">
                      <Award className="h-3 w-3" />
                      <span>{cert}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Assigned Sites */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Assigned Sites</h3>
            {worker.sites && worker.sites.length > 0 ? (
              <div className="space-y-3">
                {worker.sites.map((site) => (
                  <div key={site.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 bg-orange-100 rounded-full flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{site.name}</p>
                        <p className="text-xs text-gray-500">{site.address}</p>
                        <p className="text-xs text-gray-400 capitalize">{site.type.replace('_', ' ')}</p>
                      </div>
                    </div>
                    <Link
                      to={`/sites/${site.id}`}
                      className="text-orange-600 hover:text-orange-700 text-sm"
                    >
                      View Site
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No sites assigned to this worker yet.</p>
            )}
          </div>

          {/* Induction History */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Induction History</h3>
            {worker.inductions && worker.inductions.length > 0 ? (
              <div className="space-y-3">
                {worker.inductions.map((induction) => (
                  <div key={induction.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      {getInductionStatusIcon(induction.status)}
                      <div>
                        <p className="text-sm font-medium text-gray-900">{induction.site?.name}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(induction.created_at).toLocaleDateString()}
                          {induction.completed_at && (
                            <span> • Completed {new Date(induction.completed_at).toLocaleDateString()}</span>
                          )}
                        </p>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          induction.status === 'completed' ? 'bg-green-100 text-green-800' :
                          induction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {induction.status.charAt(0).toUpperCase() + induction.status.slice(1)}
                        </span>
                      </div>
                    </div>
                    <Link
                      to={`/inductions/${induction.id}`}
                      className="text-orange-600 hover:text-orange-700 text-sm"
                    >
                      View Details
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No inductions recorded for this worker yet.</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Stats</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Building2 className="h-5 w-5 text-orange-600" />
                  <span className="text-sm text-gray-700">Assigned Sites</span>
                </div>
                <span className="text-lg font-semibold text-gray-900">
                  {worker.sites?.length || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <ClipboardCheck className="h-5 w-5 text-green-600" />
                  <span className="text-sm text-gray-700">Completed Inductions</span>
                </div>
                <span className="text-lg font-semibold text-gray-900">
                  {worker.inductions?.filter(i => i.status === 'completed').length || 0}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Award className="h-5 w-5 text-blue-600" />
                <span className="text-sm text-gray-700">Certifications</span>
                <span className="text-lg font-semibold text-gray-900">
                  {worker.certifications?.length || 0}
                </span>
              </div>
            </div>
          </div>

          {/* Safety Status */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Safety Status</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-green-600" />
                <span className="text-sm text-green-800">Safety Compliant</span>
              </div>
              {worker.certifications && worker.certifications.length > 0 && (
                <div className="flex items-center space-x-2">
                  <Award className="h-5 w-5 text-blue-600" />
                  <span className="text-sm text-blue-800">Certified Worker</span>
                </div>
              )}
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-green-800">Active Status</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <Link
                to="/inductions"
                className="block w-full text-left p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
              >
                <div className="flex items-center">
                  <ClipboardCheck className="h-5 w-5 text-green-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-green-800">New Induction</p>
                    <p className="text-xs text-green-600">Process site induction</p>
                  </div>
                </div>
              </Link>
              <Link
                to="/sites"
                className="block w-full text-left p-3 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors"
              >
                <div className="flex items-center">
                  <Building2 className="h-5 w-5 text-orange-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-orange-800">Assign to Site</p>
                    <p className="text-xs text-orange-600">Add to new site</p>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <WorkerForm
          worker={worker}
          onSubmit={handleUpdateWorker}
          onClose={() => setShowEditModal(false)}
          loading={submitting}
        />
      )}
    </div>
  );
};